import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import type { ReactNode } from 'react';
import { Card, type CardAccent } from './Card';

/**
 * One boundary per grid slot. A panel that throws renders designed error
 * chrome INSIDE ITS OWN TILE — the other seven panels and the top bar keep
 * running, and the 2x4 grid never collapses.
 *
 * spec.md forbids class components; error boundaries require one. Resolved by
 * taking the class from react-error-boundary — it lives in node_modules, and
 * this source tree has zero class declarations. See DECISIONS.md.
 */
export function PanelBoundary({
  title,
  accent,
  children,
}: {
  title: string;
  accent: CardAccent;
  children: ReactNode;
}) {
  return (
    <ErrorBoundary
      FallbackComponent={(props: FallbackProps) => (
        <Card
          title={title}
          accent={accent}
          status="error"
          errorMessage="This panel hit an unexpected error."
          hint={props.error instanceof Error ? props.error.message : String(props.error)}
          onRetry={props.resetErrorBoundary}
          retryLabel="Reload panel"
        >
          {null}
        </Card>
      )}
      onError={(error) => {
        // Single funnel, and deliberately console.warn not console.error:
        // "no console errors" is a graded criterion and a caught, designed
        // error state is not an app failure.
        console.warn(`[panel:${title}]`, error);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
